/**
 * GA4 adapter for typed analytics events
 * Maps provider-agnostic AnalyticsEvent to GA4 format
 */

import type { AnalyticsEvent } from '../../../types/analytics-events';
import { isClientAnalyticsAllowed } from '../../cookie-consent/client-analytics-allowed';

// Declare window.gtag for GA4
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    gtag_ready?: boolean;
  }
}

/**
 * Map AnalyticsEvent to GA4 event name
 */
function mapEventNameToGA4(event: AnalyticsEvent): string {
  // Every event keeps its own name. Do NOT map onto GA4 built-in names:
  // 'page_view' (hence PageViewEvent's name being 'spa_page_view', not 'page_view') would
  // inflate page views, and 'click' collides with GA4 enhanced-measurement outbound clicks.
  return event.name;
}

/**
 * Map AnalyticsEvent params to GA4 parameters
 */
export function mapParamsToGA4(event: AnalyticsEvent): Record<string, any> {
  const baseParams: Record<string, any> = {};
  
  switch (event.name) {
    case 'job_view':
      baseParams.job_id = event.params.job_id;
      baseParams.job_title = event.params.job_title;
      baseParams.company_id = event.params.company_id;
      if (event.params.company_name) baseParams.company_name = event.params.company_name;
      if (event.params.location_label) baseParams.location_label = event.params.location_label;
      if (event.params.is_remote !== undefined) baseParams.is_remote = event.params.is_remote;
      if (event.params.has_salary_insight !== undefined) baseParams.has_salary_insight = event.params.has_salary_insight;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'job';
      baseParams.event_label = event.params.job_title;
      break;
      
    case 'company_view':
      baseParams.company_id = event.params.company_id;
      if (event.params.company_name) baseParams.company_name = event.params.company_name;
      if (event.params.industry) baseParams.industry = event.params.industry;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'company';
      baseParams.event_label = event.params.company_name || event.params.company_id;
      break;
      
    case 'srp_view':
      baseParams.srp_id = event.params.srp_id;
      if (event.params.query_text) baseParams.query_text = event.params.query_text;
      if (event.params.location_label) baseParams.location_label = event.params.location_label;
      if (event.params.result_count !== undefined) baseParams.result_count = event.params.result_count;
      if (event.params.filters_applied) baseParams.filters_applied = event.params.filters_applied;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'search';
      baseParams.event_label = event.params.query_text || 'srp_view';
      break;
      
    case 'salary_insight_view':
      baseParams.job_id = event.params.job_id;
      baseParams.company_id = event.params.company_id;
      if (event.params.has_exact_salary !== undefined) baseParams.has_exact_salary = event.params.has_exact_salary;
      if (event.params.has_estimated_salary !== undefined) baseParams.has_estimated_salary = event.params.has_estimated_salary;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'job';
      baseParams.event_label = 'salary_insight_view';
      break;
      
    case 'job_click':
      baseParams.job_id = event.params.job_id;
      baseParams.job_title = event.params.job_title;
      baseParams.company_id = event.params.company_id;
      if (event.params.position !== undefined) baseParams.position = event.params.position;
      // NOT `source`: GA4 reserves that key for campaign attribution — see the comment on
      // JobClickEvent.params.click_source in types/analytics-events.ts.
      if (event.params.click_source) baseParams.click_source = event.params.click_source;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'job';
      baseParams.event_label = event.params.job_title;
      baseParams.value = event.params.position;
      break;

    case 'job_mark_viewed':
      baseParams.job_id = event.params.job_id;
      baseParams.job_title = event.params.job_title;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'job';
      baseParams.event_label = event.params.job_title;
      break;

    case 'salary_insight_interaction':
      baseParams.interaction_type = event.params.interaction_type;
      if (event.params.job_id) baseParams.job_id = event.params.job_id;
      if (event.params.company_id) baseParams.company_id = event.params.company_id;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'job';
      baseParams.event_label = event.params.interaction_type;
      break;
      
    case 'job_save':
    case 'job_unsave':
    case 'job_share':
      baseParams.job_id = event.params.job_id;
      baseParams.job_title = event.params.job_title;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'job';
      baseParams.event_label = event.params.job_title;
      break;
      
    case 'filter_add':
    case 'filter_remove':
      baseParams.filter_key = event.params.filter_key;
      baseParams.filter_value = event.params.filter_value;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'search';
      baseParams.event_label = `${event.params.filter_key}: ${event.params.filter_value}`;
      break;
      
    case 'sort_change':
      baseParams.sort_type = event.params.sort_type;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'search';
      baseParams.event_label = event.params.sort_type;
      break;
      
    case 'search_pagination':
      baseParams.page_number = event.params.page_number;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'navigation';
      baseParams.event_label = `Page ${event.params.page_number}`;
      break;
      
    case 'search_suggestion_click':
      baseParams.search_term = event.params.suggestion;
      if (event.params.facet_key) baseParams.facet_key = event.params.facet_key;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'search';
      baseParams.event_label = event.params.suggestion;
      break;
      
    case 'company_link_click':
      baseParams.company_id = event.params.company_id;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'interaction';
      baseParams.event_label = event.params.company_id;
      break;

    case 'company_follow':
    case 'company_unfollow':
      baseParams.company_id = event.params.company_id;
      if (event.params.surface) baseParams.surface = event.params.surface;
      if (event.params.locale) baseParams.locale = event.params.locale;
      if (event.params.logged_in !== undefined) baseParams.logged_in = event.params.logged_in;
      baseParams.event_category = 'company';
      baseParams.event_label = event.params.company_id;
      break;

    case 'company_bulk_follow':
      baseParams.count = event.params.count;
      if (event.params.surface) baseParams.surface = event.params.surface;
      if (event.params.locale) baseParams.locale = event.params.locale;
      if (event.params.logged_in !== undefined) baseParams.logged_in = event.params.logged_in;
      baseParams.event_category = 'company';
      baseParams.event_label = 'company_bulk_follow';
      break;

    case 'saved_search_create':
      if (event.params.locale) baseParams.locale = event.params.locale;
      if (event.params.logged_in !== undefined) baseParams.logged_in = event.params.logged_in;
      baseParams.event_category = 'search';
      baseParams.event_label = 'saved_search_create';
      break;

    case 'spa_page_view':
      if (event.params.page_type) baseParams.page_type = event.params.page_type;
      if (event.params.job_id) baseParams.job_id = event.params.job_id;
      if (event.params.company_id) baseParams.company_id = event.params.company_id;
      if (event.params.srp_id) baseParams.srp_id = event.params.srp_id;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'navigation';
      break;
      
    case 'job_visibility_change':
      baseParams.job_id = event.params.job_id;
      baseParams.action = event.params.action;
      if (event.params.company_id) baseParams.company_id = event.params.company_id;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'job';
      baseParams.event_label = event.params.action;
      break;

    case 'job_pipeline_stage_change':
      baseParams.job_id = event.params.job_id;
      baseParams.to_stage = event.params.to_stage;
      if (event.params.from_stage) baseParams.from_stage = event.params.from_stage;
      baseParams.surface = event.params.surface;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'tools';
      baseParams.event_label = event.params.to_stage;
      break;

    case 'job_apply_form_open':
      if (event.params.job_id) baseParams.job_id = event.params.job_id;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'tools';
      baseParams.event_label = 'job_apply_form_open';
      break;

    case 'tailor_cv_interaction':
      baseParams.action = event.params.action;
      if (event.params.job_id) baseParams.job_id = event.params.job_id;
      if (event.params.company_id) baseParams.company_id = event.params.company_id;
      if (event.params.locale) baseParams.locale = event.params.locale;
      if (event.params.source) baseParams.source = event.params.source;
      baseParams.event_category = 'job';
      baseParams.event_label = event.params.action;
      break;

    case 'cover_letter_interaction':
      baseParams.action = event.params.action;
      if (event.params.job_id) baseParams.job_id = event.params.job_id;
      if (event.params.company_id) baseParams.company_id = event.params.company_id;
      if (event.params.locale) baseParams.locale = event.params.locale;
      if (event.params.tone) baseParams.tone = event.params.tone;
      if (event.params.ungrounded_count !== undefined) baseParams.ungrounded_count = event.params.ungrounded_count;
      if (event.params.overclaim_count !== undefined) baseParams.overclaim_count = event.params.overclaim_count;
      if (event.params.edited !== undefined) baseParams.edited = event.params.edited;
      baseParams.event_category = 'job';
      baseParams.event_label = event.params.action;
      break;

    case 'application_kit_interaction':
      baseParams.action = event.params.action;
      if (event.params.job_id) baseParams.job_id = event.params.job_id;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'tools';
      baseParams.event_label = event.params.action;
      break;

    case 'job_contact_email_reveal':
      baseParams.job_id = event.params.job_id;
      if (event.params.company_id) baseParams.company_id = event.params.company_id;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'job';
      baseParams.event_label = 'job_contact_email_reveal';
      break;

    case 'job_detail_tab_change':
      baseParams.job_id = event.params.job_id;
      baseParams.tab = event.params.tab;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'navigation';
      baseParams.event_label = event.params.tab;
      break;

    case 'locale_change':
      baseParams.to_locale = event.params.to_locale;
      if (event.params.from_locale) baseParams.from_locale = event.params.from_locale;
      baseParams.surface = event.params.surface;
      baseParams.event_category = 'navigation';
      baseParams.event_label = event.params.to_locale;
      break;

    case 'job_alert_cta_interaction':
      baseParams.action = event.params.action;
      if (event.params.locale) baseParams.locale = event.params.locale;
      if (event.params.channels) baseParams.channels = event.params.channels;
      baseParams.event_category = 'job_alerts';
      baseParams.event_label = event.params.action;
      break;

    case 'job_alert_subscription_change':
      baseParams.action = event.params.action;
      if (event.params.frequency) baseParams.frequency = event.params.frequency;
      if (event.params.comms_locale) baseParams.comms_locale = event.params.comms_locale;
      if (event.params.locale) baseParams.locale = event.params.locale;
      if (event.params.channels) baseParams.channels = event.params.channels;
      // Subscribing is a marketplace-driven conversion (recurring re-engagement
      // signal); unsubscribe/preference_change are just bookkeeping, same split
      // as job_apply_conversion vs job_application_logged.
      baseParams.event_category = event.params.action === 'subscribe' ? 'conversion' : 'job_alerts';
      baseParams.event_label = event.params.action;
      break;

    case 'saved_search_alert_toggle':
      baseParams.enabled = event.params.enabled;
      if (event.params.saved_search_id) baseParams.saved_search_id = event.params.saved_search_id;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'job_alerts';
      baseParams.event_label = event.params.enabled ? 'enabled' : 'disabled';
      break;

    case 'saved_search_management':
      baseParams.action = event.params.action;
      if (event.params.saved_search_id) baseParams.saved_search_id = event.params.saved_search_id;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'tools';
      baseParams.event_label = event.params.action;
      break;

    case 'cookie_consent_accepted':
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'consent';
      baseParams.event_label = 'cookie_consent_accepted';
      break;

    case 'feedback_submit':
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'interaction';
      baseParams.event_label = 'feedback_submit';
      break;

    case 'auth_interaction':
      baseParams.action = event.params.action;
      baseParams.surface = event.params.surface;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'auth';
      baseParams.event_label = event.params.action;
      break;

    case 'sign_in_prompt_shown':
      baseParams.surface = event.params.surface;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'auth';
      baseParams.event_label = 'sign_in_prompt_shown';
      break;

    case 'account_created':
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'conversion';
      baseParams.event_label = 'account_created';
      // Highest-weighted conversion: an account is worth far more than a
      // single tracked apply (job_apply_conversion sends value 1).
      baseParams.value = 10;
      baseParams.currency = 'EUR';
      break;

    case 'sign_in_complete':
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'auth';
      baseParams.event_label = 'sign_in_complete';
      break;

    case 'pwa_installed':
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'interaction';
      baseParams.event_label = 'pwa_installed';
      break;

    case 'pwa_install_prompt_click':
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'interaction';
      baseParams.event_label = 'pwa_install_prompt_click';
      break;

    case 'srp_map_interaction':
      baseParams.action = event.params.action;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'search';
      baseParams.event_label = event.params.action;
      break;

    case 'nav_menu_open':
      baseParams.menu = event.params.menu;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'navigation';
      baseParams.event_label = event.params.menu;
      break;

    case 'profile_media_action':
      baseParams.action = event.params.action;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'profile';
      baseParams.event_label = event.params.action;
      break;

    case 'cv_studio_action':
      baseParams.action = event.params.action;
      baseParams.guest = event.params.guest;
      if (event.params.rule) baseParams.rule = event.params.rule;
      if (event.params.template) baseParams.template = event.params.template;
      if (typeof event.params.score === 'number') baseParams.score = event.params.score;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'cv_studio';
      baseParams.event_label = event.params.action;
      break;

    case 'profile_section_item_change':
      baseParams.section = event.params.section;
      baseParams.action = event.params.action;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'profile';
      baseParams.event_label = `${event.params.section}_${event.params.action}`;
      break;

    case 'profile_field_change':
      baseParams.field = event.params.field;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'profile';
      baseParams.event_label = event.params.field;
      break;

    case 'profile_match_weight_change':
      baseParams.slider_name = event.params.slider_name;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'profile';
      baseParams.event_label = event.params.slider_name;
      break;

    case 'profile_preview_open':
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'profile';
      baseParams.event_label = 'profile_preview_open';
      break;

    case 'breadcrumb_home_click':
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'navigation';
      baseParams.event_label = 'breadcrumb_home_click';
      break;

    case 'theme_change':
      baseParams.theme = event.params.theme;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'interaction';
      baseParams.event_label = event.params.theme;
      break;

    case 'plus_gate_viewed':
    case 'plus_upsell_opened':
    case 'plus_pricing_viewed':
    case 'plus_checkout_started':
    case 'plus_checkout_completed':
    case 'plus_checkout_abandoned':
    case 'plus_renewed':
    case 'plus_cancelled':
    case 'plus_withdrawn':
    case 'plus_free_form_opened':
    case 'plus_free_form_sent':
    case 'plus_grant_created':
    case 'plus_apply_address_copied':
    case 'plus_apply_for_me_started':
    case 'plus_apply_address_rotated':
    case 'plus_limit_reached':
    case 'plus_upgrade_started':
    case 'plus_hired_keep_offered':
    case 'plus_hired_keep_accepted':
      if (event.params.discount_percent !== undefined) baseParams.discount_percent = event.params.discount_percent;
      if (event.params.feature) baseParams.feature = event.params.feature;
      if (event.params.surface) baseParams.surface = event.params.surface;
      if (event.params.plan) baseParams.plan = event.params.plan;
      if (event.params.tier) baseParams.tier = event.params.tier;
      if (event.params.from) baseParams.from_tier = event.params.from;
      if (event.params.to) baseParams.to_tier = event.params.to;
      if (event.params.reason) baseParams.reason = event.params.reason;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'plus';
      baseParams.event_label = event.params.feature ?? event.params.plan ?? event.params.reason ?? event.name;
      break;

    case 'trustpilot_prompt':
      baseParams.action = event.params.action;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'engagement';
      baseParams.event_label = event.params.action;
      break;
  }

  return baseParams;
}

/**
 * Sets the `login_status` user property so GA4 reports can be segmented by
 * signed-in vs anonymous traffic. Not personally identifying — just a
 * 'logged_in' | 'logged_out' state, not an account ID (unlike GA4 User-ID).
 * Register it once as a user-scoped custom dimension in GA4 Admin > Custom
 * definitions (dimension name "Login Status", user property "login_status")
 * to use it in Explore/reports.
 */
export function setLoginStatusUserProperty(isLoggedIn: boolean): void {
  if (typeof window === 'undefined') return;
  if (!isClientAnalyticsAllowed()) return;
  if (!window.gtag) return;
  window.gtag('set', 'user_properties', {
    login_status: isLoggedIn ? 'logged_in' : 'logged_out',
  });
}

/**
 * Send a GA4 event by raw name/params, outside the typed AnalyticsEvent union
 * (e.g. the debounced `search` / `search_no_results` analytics).
 */
export function sendGA4RawEvent(eventName: string, params: Record<string, unknown>): boolean {
  if (typeof window === 'undefined') return false;
  if (!isClientAnalyticsAllowed()) return false;
  if (!window.gtag) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[GA4 Adapter] window.gtag not available. Event:', eventName);
    }
    return false;
  }
  try {
    window.gtag('event', eventName, params);
    return true;
  } catch (error) {
    console.error('[GA4 Adapter] Error sending raw event:', error, eventName);
    return false;
  }
}

/**
 * Send an AnalyticsEvent to GA4
 * Reuses existing window.gtag mechanism
 */
export function sendGA4Event(event: AnalyticsEvent): boolean {
  // Check browser availability
  if (typeof window === 'undefined') {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[GA4 Adapter] Cannot send event in non-browser environment:', event.name);
    }
    return false;
  }

  if (!isClientAnalyticsAllowed()) {
    return false;
  }
  
  // Check GA4 readiness
  if (!window.gtag) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[GA4 Adapter] window.gtag not available. Event:', event.name);
    }
    return false;
  }
  
  try {
    const ga4EventName = mapEventNameToGA4(event);
    const ga4Params = mapParamsToGA4(event);
    
    // Development logging
    if (process.env.NODE_ENV === 'development') {
      console.log('[GA4 Adapter] Sending event:', {
        eventName: ga4EventName,
        params: ga4Params,
        originalEvent: event.name,
      });
    }
    
    // Send to GA4 using existing gtag mechanism
    window.gtag('event', ga4EventName, ga4Params);
    
    return true;
  } catch (error) {
    console.error('[GA4 Adapter] Error sending event:', error, event);
    return false;
  }
}

